import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { TextField, Button, Box } from "@mui/material";
import { styled, keyframes } from "@mui/system";
import AudioPlayer from "./AudioPlayer";

// Create the shake keyframes animation
const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
  20%, 40%, 60%, 80% { transform: translateX(10px); }
`;

// Create a styled TextField that can shake
const ShakingTextField = styled(TextField)(({ isShaking }) => ({
  animation: isShaking ? `${shakeAnimation} 0.5s ease-in-out` : "none",
  background: 'white'
}));

const TextAnswerInput = forwardRef(({
  onSubmitAnswer,
  waitingToProceed,
  onProceed,
  audioData,
  question,
  isRetrying = false,
  hideAudioPlayer = false, // New prop to control audio player visibility
}, ref) => {
  const [input, setInput] = useState("");
  const [shake, setShake] = useState(false);
  const [showAudio, setShowAudio] = useState(false);

  const inputRef = useRef(null);

  // Check if we should show the audio player
  useEffect(() => {
    // Only show audio player if not explicitly hidden and conditions are met
    if (!hideAudioPlayer && audioData && question) {
      const questionLowerCase = question.toLowerCase();
      if (questionLowerCase.includes("listen") || questionLowerCase.includes("audio")) {
        setShowAudio(true);
      }
    }
  }, [audioData, question, hideAudioPlayer]);

  // Expose methods to parent components
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    reset: () => {
      setInput("");
    },
    shake: () => {
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  }));

  // Effect to handle retry logic
  useEffect(() => {
    if (isRetrying) {
      setInput("");
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [isRetrying]);

  // Effect to focus the input when component renders or when a new question is presented
  useEffect(() => {
    if (!waitingToProceed && inputRef.current) {
      const timeoutId = setTimeout(() => inputRef.current.focus(), 100);
      return () => clearTimeout(timeoutId);
    }
  }, [waitingToProceed, question]);

  // Additional effect to ensure auto-focus on initial render
  useEffect(() => {
    if (inputRef.current) {
      const timeoutId = setTimeout(() => inputRef.current.focus(), 100);
      return () => clearTimeout(timeoutId);
    }
  }, []); // Empty dependency array ensures this runs only on mount

  const handleSubmission = () => {
    if (input.trim() === "") {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }
    onSubmitAnswer(input);
  };

  const handleProceedAction = () => {
    onProceed();
    setInput("");
  };

  // Keyboard shortcut for submission/proceed
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // Prevent default browser action (like form submission)
      e.preventDefault();
      // Prevent the event from bubbling up or triggering other handlers
      e.stopPropagation();
      handleClick();
    }
  };

  const handleClick = () => {
    if (waitingToProceed) {
      handleProceedAction();
    } else {
      handleSubmission();
    }
  };

  // Determine visual state
  const isInputBlocked = waitingToProceed;
  
  // Determine button text - only show "Processing..." if submitted but NOT waiting to proceed
  // This prevents the long "Processing..." state for correct answers
  const getButtonText = () => {
    if (waitingToProceed) return "Proceed";
    return "Submit";
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Conditionally render AudioPlayer only if not hidden */}
      {showAudio && audioData && !hideAudioPlayer && (
        <AudioPlayer audioData={audioData} questionText={question} />
      )}

      <ShakingTextField
        fullWidth
        label="Your Answer"
        variant="outlined"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        // Make the input read-only when blocked or processing
        InputProps={{ readOnly: isInputBlocked }}
        inputRef={inputRef}
        isShaking={shake}
        sx={{
          opacity: 1,
          '& .MuiInputBase-input': {
            color: (isInputBlocked) ? '#666' : 'inherit'
          }
        }}
      />
      <center>
        <Button
          variant="contained"
          onClick={handleClick}
          sx={{ mt: 2 }}
          color="primary"
          disabled={false}  // Fixed: Button should never be disabled
        >
          {getButtonText()}
        </Button>
      </center>
    </Box>
  );
});

TextAnswerInput.displayName = 'TextAnswerInput';

export default TextAnswerInput;